import React, { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { withRouter } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UserIcon } from '@peracto/peracto-ui'
import { useAuth } from '@peracto/peracto-auth'
import { useConfig } from '@peracto/peracto-config'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import defaultMenu from './menuItems'

import classNames from 'classnames'
import * as S from './styled'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight'
import { faPhotoVideo } from '@fortawesome/pro-regular-svg-icons/faPhotoVideo'

const Menu = ({ isOpen, setIsOpen, location: { pathname } }) => {
    const [openMenus, setOpenMenus] = useState({})

    const config = useConfig()
    const menu = config.get('menu', defaultMenu)
    const features = config.get('features', {})

    const { user, logout } = useAuth()
    const userRoles = user?.profile?.roles || []

    const userGroup = user?.profile?.user_group || []

    const token = window.localStorage.getItem('token')

    const handleMenuOnResize = () => {
        // Collapse the menu when navigating on tablet / mobile
        if (window.matchMedia('(max-width: 991px)').matches) {
            setOpenMenus({})
            setIsOpen(false)
        } else {
            setIsOpen(true)
        }
    }

    useEffect(() => {
        handleMenuOnResize()
    }, [pathname])

    window.addEventListener('resize', debounce(handleMenuOnResize, 200))

    const handleMenuToggle = menu => {
        setOpenMenus({
            ...openMenus,
            [menu]: !openMenus[menu],
        })
    }

    const getFeatureFlagValue = feature =>
        feature.split('.').reduce((o, i) => {
            if (!!o === false || !o.hasOwnProperty(i)) {
                return null
            }
            return o[i]
        }, features)

    const menuItems = sortBy(Object.values(menu), ['sortOrder'])

    return (
        <>
            <S.Styled
                className={classNames({
                    collapsed: !isOpen,
                })}
            >
                <nav className="nav-list">
                    {menuItems?.length > 0 &&
                        menuItems.map((menuItem, lvl1idx) => {
                            if (
                                (!!menuItem.roles &&
                                    !menuItem?.roles?.some(role => userRoles.includes(role))) ||
                                (menuItem?.feature &&
                                    !!getFeatureFlagValue(menuItem?.feature) === false) ||
                                (menuItem?.userGroups?.length > 0 &&
                                    !menuItem.userGroups.includes(userGroup))
                            ) {
                                return null
                            }
                            if (menuItem?.path && !!menuItem?.children === false) {
                                return (
                                    <NavLink
                                        key={`menu_${lvl1idx}`}
                                        to={menuItem.path}
                                        activeClassName="active"
                                        exact={true}
                                        className="nav-list-item"
                                        data-testid={`nav-${menuItem.label
                                            .toLowerCase()
                                            .replaceAll(' ', '-')}`}
                                    >
                                        <span>
                                            {menuItem.icon && (
                                                <FontAwesomeIcon
                                                    icon={menuItem.icon}
                                                    className="nav-icon"
                                                />
                                            )}
                                            {menuItem.label}
                                        </span>
                                    </NavLink>
                                )
                            } else {
                                if (!!menuItem.children === false) {
                                    return null
                                }
                                const childMenuItems = sortBy(Object.values(menuItem.children), [
                                    'sortOrder',
                                ])

                                if (
                                    (!!menuItem.roles &&
                                        !menuItem?.roles?.some(role => userRoles.includes(role))) ||
                                    (menuItem?.feature &&
                                        !!getFeatureFlagValue(menuItem?.feature) === false) ||
                                    (menuItem?.userGroups?.length > 0 &&
                                        !menuItem.userGroups.includes(userGroup))
                                ) {
                                    return null
                                }

                                return (
                                    <React.Fragment key={`menu_${lvl1idx}`}>
                                        <a
                                            onClick={() => handleMenuToggle(menuItem.label)}
                                            className="nav-list-item"
                                            data-testid={`nav-${menuItem.label
                                                .toLowerCase()
                                                .replaceAll(' ', '-')}`}
                                        >
                                            <span>
                                                <FontAwesomeIcon
                                                    icon={menuItem.icon}
                                                    className="nav-icon"
                                                    data-testid={`nav-icon-${menuItem.label
                                                        .toLowerCase()
                                                        .replaceAll(' ', '-')}`}
                                                />
                                                {menuItem.label}
                                            </span>
                                            <FontAwesomeIcon
                                                icon={faChevronRight}
                                                className={classNames('menu-icon', {
                                                    'menu-icon__open': openMenus[menuItem.label],
                                                })}
                                                data-testid={`nav-chevron-${menuItem.label
                                                    .toLowerCase()
                                                    .replaceAll(' ', '-')}`}
                                            />
                                        </a>

                                        <div
                                            className={classNames('nav-sublist', {
                                                'nav-sublist__active': openMenus[menuItem.label],
                                            })}
                                        >
                                            {childMenuItems.map((menuItemChild, lvl2Idx) => {
                                                if (
                                                    (!!menuItemChild.roles &&
                                                        !menuItemChild?.roles?.some(role =>
                                                            userRoles.includes(role)
                                                        )) ||
                                                    (menuItemChild?.feature &&
                                                        !!getFeatureFlagValue(
                                                            menuItemChild?.feature
                                                        ) === false) ||
                                                    (menuItemChild?.userGroups?.length > 0 &&
                                                        !menuItemChild.userGroups.includes(
                                                            userGroup
                                                        ))
                                                ) {
                                                    return null
                                                }
                                                if (
                                                    menuItemChild?.path &&
                                                    !!menuItemChild?.children === false
                                                ) {
                                                    return (
                                                        <NavLink
                                                            key={`menu_${lvl1idx}_${lvl2Idx}`}
                                                            to={menuItemChild.path}
                                                            activeClassName="active"
                                                            className="nav-list-item"
                                                            data-testid={`nav-${menuItem.label}-${menuItemChild.label}`
                                                                .toLowerCase()
                                                                .replaceAll(' ', '-')}
                                                        >
                                                            {menuItemChild.label}
                                                        </NavLink>
                                                    )
                                                } else {
                                                    if (!!menuItemChild.children === false) {
                                                        return null
                                                    }
                                                    const grandchildMenuItems = sortBy(
                                                        Object.values(menuItemChild.children),
                                                        ['sortOrder']
                                                    )
                                                    return (
                                                        <React.Fragment
                                                            key={`menu_${lvl1idx}_${lvl2Idx}`}
                                                        >
                                                            <a
                                                                onClick={() =>
                                                                    handleMenuToggle(
                                                                        menuItemChild.label
                                                                    )
                                                                }
                                                                className="nav-list-item"
                                                                data-testid={`nav-${menuItem.label}-${menuItemChild.label}`
                                                                    .toLowerCase()
                                                                    .replaceAll(' ', '-')}
                                                            >
                                                                <span>{menuItemChild.label}</span>
                                                                <FontAwesomeIcon
                                                                    icon={faChevronRight}
                                                                    className={classNames(
                                                                        'menu-icon',
                                                                        {
                                                                            'menu-icon__open':
                                                                                openMenus[
                                                                                    menuItemChild
                                                                                        .label
                                                                                ],
                                                                        }
                                                                    )}
                                                                    data-testid={`nav-chevron-${menuItem.label}-${menuItemChild.label}`
                                                                        .toLowerCase()
                                                                        .replaceAll(' ', '-')}
                                                                />
                                                            </a>

                                                            <div
                                                                className={classNames(
                                                                    'nav-sublist',
                                                                    'nav-sublist__lvl2',
                                                                    {
                                                                        'nav-sublist__active':
                                                                            openMenus[
                                                                                menuItemChild.label
                                                                            ],
                                                                    }
                                                                )}
                                                            >
                                                                {grandchildMenuItems.map(
                                                                    (grandchildItem, lvl3Idx) => {
                                                                        if (
                                                                            (!!grandchildItem.roles &&
                                                                                !grandchildItem?.roles?.some(
                                                                                    role =>
                                                                                        userRoles.includes(
                                                                                            role
                                                                                        )
                                                                                )) ||
                                                                            (grandchildItem?.feature &&
                                                                                !!getFeatureFlagValue(
                                                                                    grandchildItem?.feature
                                                                                ) === false) ||
                                                                            (grandchildItem
                                                                                ?.userGroups
                                                                                ?.length > 0 &&
                                                                                !grandchildItem.userGroups.includes(
                                                                                    userGroup
                                                                                ))
                                                                        ) {
                                                                            return null
                                                                        }

                                                                        return (
                                                                            <NavLink
                                                                                key={`menu_${lvl1idx}_${lvl2Idx}_${lvl3Idx}`}
                                                                                to={
                                                                                    grandchildItem.path
                                                                                }
                                                                                activeClassName="active"
                                                                                className="nav-list-item"
                                                                                data-testid={`nav-${menuItem.label}-${menuItemChild.label}-${grandchildItem.label}`
                                                                                    .toLowerCase()
                                                                                    .replaceAll(
                                                                                        ' ',
                                                                                        '-'
                                                                                    )}
                                                                            >
                                                                                {
                                                                                    grandchildItem.label
                                                                                }
                                                                            </NavLink>
                                                                        )
                                                                    }
                                                                )}
                                                            </div>
                                                        </React.Fragment>
                                                    )
                                                }
                                            })}
                                        </div>
                                    </React.Fragment>
                                )
                            }
                        })}
                </nav>
            </S.Styled>

            <S.Styled
                className={classNames({
                    collapsed: !isOpen,
                })}
            >
                <div className="nav-list">
                    <button
                        className="nav-list-item !flex justify-space-between"
                        data-testid="nav-file-manager"
                        style={{
                            background: 'transparent',
                            border: 'none',
                        }}
                        onClick={e => {
                            e.preventDefault()
                            if (window?.CKFinder) {
                                window.CKFinder.modal({
                                    chooseFiles: false,
                                    displayFoldersPanel: false,
                                    removeModules: 'Maximize,EditImage',
                                    pass: 'token',
                                    token: token,
                                    width: 800,
                                    height: 600,
                                })

                                return false
                            }
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faPhotoVideo}
                            className="nav-icon"
                            data-testid="nav-icon-file-manager"
                        />
                        <span>File Manager</span>
                    </button>
                </div>
                <div className="!border-t !border-t-[#dee2e6]">
                    <nav className="nav-list !pb-2 lg:!pb-0">
                        <UserIcon className="nav-list-item !pb-0" data-testid="user-profile" />
                        <a
                            className="cursor-pointer nav-list-item !py-0 !self-end"
                            data-testid="logout"
                            onClick={() => logout()}
                        >
                            <span className="brand-color">Logout</span>
                        </a>
                    </nav>
                </div>
            </S.Styled>
        </>
    )
}

export default withRouter(Menu)
